import { clsx } from "clsx";
import type { MouseEvent, SVGProps } from "react";
import "@/components/ButtonPrimitive/ButtonPrimitive.css";
import { isValidIconName } from "@/icons/icon-names";

const sizeMap: Record<string, string> = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
  "2xl": "w-10 h-10",
};

interface ExtendedIconProps extends Omit<SVGProps<SVGSVGElement>, "name" | "size" | "className"> {
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  className?: string;
  title?: string;
  desc?: string;
}

export function Icon({
  name,
  size = "md",
  className = "",
  title,
  desc,
  ...props
}: ExtendedIconProps) {
  if (import.meta.env.DEV && !isValidIconName(name)) {
  }

  const sizeClass = sizeMap[size] || sizeMap.md;
  const iconId = `icon-${name}`;

  return (
    <svg
      className={clsx(
        "inline-block shrink-0 fill-none stroke-current align-middle",
        sizeClass,
        className,
      )}
      aria-hidden={!title && !desc}
      role={title || desc ? "img" : undefined}
      {...props}
    >
      {title && <title>{title}</title>}
      {desc && <desc>{desc}</desc>}
      <use href={`#${iconId}`} />
    </svg>
  );
}

Icon.displayName = "Icon";

// Export common icon presets for consistency
interface IconButtonProps {
  name: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  className?: string;
}

const iconButtonBoxSize: Record<"sm" | "md" | "lg", string> = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

export function IconButton({ name, onClick, title, size = "md", className = "" }: IconButtonProps) {
  const boxSize = size === "sm" || size === "lg" ? size : "md";

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx("ds-btn ds-btn-ghost rounded-md p-0", iconButtonBoxSize[boxSize])}
      aria-label={title}
    >
      <Icon name={name} size={size} className={className} />
    </button>
  );
}

IconButton.displayName = "IconButton";
